import React, { useEffect, useState } from "react";
import { proxyAPI } from "../proxyAPI";
import { toast } from "react-toastify";

type Variable = {
  Field: string;
  Label: string;
  Format: string;
  Required: boolean;
  Multiple: boolean;
  IsEnum: boolean;
  EnumOptions?: string[];
  Min?: number;
  Max?: number;
};

type Project = {
  Uuid: string;
  Label: string;
  Description: string;
  Reference: string;
  Variables?: Variable[];
  Examples: {
    [key: string]: string[];
  };
  // add other properties as needed
};

const variableExamples = [
  { format: "text", example: "text", multipleExample: "text-2" },
  { format: "short-text", example: "text", multipleExample: "text-2" },
  { format: "number", example: "1", multipleExample: "2" },
  {
    format: "json",
    example: '{"key": "value"}',
    multipleExample: '{"key-2": "value-2"}',
  },
  {
    format: "email",
    example: "email@example.com",
    multipleExample: "email-2@example.com",
  },
  {
    format: "sms",
    example: "+44 1234567890",
    multipleExample: "+44 1234567891",
  },
  {
    format: "voice",
    example: "+44 1234567890",
    multipleExample: "+44 1234567891",
  },
  { format: "date", example: "2023-01-01", multipleExample: "2023-01-02" },
  { format: "time", example: "12:00", multipleExample: "12:01" },
  {
    format: "dateTime",
    example: "2023-01-01 12:00",
    multipleExample: "2023-01-02 12:01",
  },
];

export default function ProjectInputVariables({ uuid }: { uuid: string }) {
  const [project, setProject] = useState<Project>({
    Uuid: "",
    Label: "",
    Description: "",
    Reference: "",
    Variables: [],
    Examples: {},
  });

  const loadProject = async (ofs = 0) => {
    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: `/http-info/${uuid}`,
      setLoading: () => {},
    });
    if (data) {
      console.log(data);
      setProject(data);
    }
  };

  useEffect(() => {
    if (uuid) {
      loadProject();
    }
  }, [uuid]);

  const inputArguments =
    project?.Variables?.filter(
      (arg: Variable) => !["contact", "event", "asset"].includes(arg.Format)
    ) || [];

  const getExampleJson = () =>
    inputArguments.reduce((acc: any, arg: Variable) => {
      return {
        ...acc,
        [arg.Field]: [
          `${
            arg.IsEnum && arg.EnumOptions && arg.EnumOptions.length
              ? arg.EnumOptions[0]
              : variableExamples.find((v) => v.format === arg.Format)
                  ?.example || arg.Format
          }`,
          ...(arg.Multiple
            ? [
                `${
                  arg.IsEnum && arg.EnumOptions && arg.EnumOptions.length
                    ? arg.EnumOptions[1] || arg.EnumOptions[0]
                    : variableExamples.find((v) => v.format === arg.Format)
                        ?.multipleExample || arg.Format + "-2"
                }`,
              ]
            : []),
        ],
      };
    }, {});

  return (
    <div>
      Project label: {project.Label}
      <div>
        <div>
          {inputArguments.length ? (
            <>
              <div className="bg-white p-2 rounded-2">
                <button
                  className="btn btn-outline-secondary border-0 float-end"
                  onClick={async () => {
                    const res = await navigator.clipboard.writeText(
                      JSON.stringify(getExampleJson(), null, 2)
                    );
                    toast.success("Copied to clipboard");
                  }}
                >
                  copy
                </button>
                {/* <pre>{JSON.stringify(getExampleJon(), null, 2)}</pre> */}
                <div>{`{`}</div>
                {inputArguments.map((arg) => (
                  <div
                    className="ms-3"
                    key={arg.Field}
                    style={{ marginBottom: 10 }}
                  >
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      // {arg.Label}{" "}
                      <span className="badge bg-secondary me-2">{arg.Format}</span>
                      {arg.Required ? (
                        <span className="badge bg-secondary me-2">required</span>
                      ) : (
                        ""
                      )}
                      {arg.Multiple ? (
                        <span className="badge bg-secondary me-2">multiple</span>
                      ) : (
                        ""
                      )}
                      {arg.IsEnum ? (
                        <span className="badge bg-secondary me-2">select</span>
                      ) : (
                        ""
                      )}
                      {arg.IsEnum &&
                      arg.EnumOptions &&
                      arg.EnumOptions.length ? (
                        <span className="badge bg-secondary me-2">
                          options: ${arg.EnumOptions.join(", ")}
                        </span>
                      ) : (
                        ""
                      )}
                      {arg.Min !== undefined ? (
                        <span className="badge bg-secondary me-2">
                          min: {arg.Min}
                        </span>
                      ) : (
                        ""
                      )}
                      {arg.Max !== undefined ? (
                        <span className="badge bg-secondary me-2">
                          max: {arg.Max}
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                    <div>
                      <strong>"{arg.Field}": [</strong>
                    </div>
                    <div className="ms-3">
                      "
                      {arg.IsEnum && arg.EnumOptions && arg.EnumOptions.length
                        ? arg.EnumOptions[0]
                        : variableExamples.find((v) => v.format === arg.Format)
                            ?.example || arg.Format}
                      ",
                    </div>
                    {arg.Multiple ? (
                      <div className="ms-3">
                        "
                        {arg.IsEnum && arg.EnumOptions && arg.EnumOptions.length
                          ? arg.EnumOptions[1] || arg.EnumOptions[0]
                          : variableExamples.find(
                              (v) => v.format === arg.Format
                            )?.multipleExample || arg.Format + "-2"}
                        ",
                      </div>
                    ) : null}
                    <div>
                      <strong>],</strong>
                    </div>
                  </div>
                ))}
                <div>{`}`}</div>
              </div>
            </>
          ) : (
            <div>No variables found.</div>
          )}
        </div>
      </div>
    </div>
  );
}
